import { sellerTable } from "../../../Component/style/sellerTable";
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import url, { SERVER_ADDRESS } from '../../../Component/server';
import Layout_Admin from '../../../Component/UI/Layout_Admin';
import AdminPageHeader from "../../../Component/UI/AdminPageHeader";
import PagingControl from '../../../Component/UI/PagingControl';
import api from '../../../Component/axios_Interceptor/api';
import LiveDetailDialog from '../LiveList/Component/LiveDetailDialog';

interface Member {
  user_seq: number; userid: string; nickname: string; name: string; phone: string;
  addr_basic: string; addr_detail: string; join_dt: string | null; last_dt: string | null;
  join_route: string | null; withdrawal_dt: string | null; is_banned: 'y' | 'n';
  ban_reason?: string | null;
}
interface Filters {
  search_key: string; search_value: string; join_route: string; member_status: string;
  ban_status: string; joined_from: string; joined_to: string;
}
const defaults: Filters = { search_key: 'nickname', search_value: '', join_route: 'all',
  member_status: 'all', ban_status: 'all', joined_from: '', joined_to: '' };
const perPage = 20;
const dateLabel = (value: string | null) => value ? value.slice(0, 10) : '—';
const activityLabel = (value: string | null) => value ? value.slice(0, 16).replace('T', ' ') : '—';
const routeLabel = (value: string | null) => value === 'normal' ? '팬버드' : value === 'awake' ? 'AWAKE' : '미확인';

export default function Userlist() {
  const navigate = useNavigate();
  const { page } = useParams();
  const isUserlist = page === 'user';
  const isChatMuted = page === 'chat-mute';
  const [draft, setDraft] = useState<Filters>({ ...defaults });
  const [query, setQuery] = useState({ filters: { ...defaults }, currentPage: 1, revision: 0 });
  const [result, setResult] = useState<{ members: Member[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [actionError, setActionError] = useState('');
  const [pendingMember, setPendingMember] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [banRequest, setBanRequest] = useState<{ members: Member[]; banned: boolean } | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banResults, setBanResults] = useState<{ member: Member; success: boolean; message?: string }[]>([]);
  const [retryRequest, setRetryRequest] = useState<{ members: Member[]; banned: boolean; reason: string } | null>(null);
  const requestId = useRef(0), mutationPending = useRef(false), mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const request = ++requestId.current;
    setLoading(true); setError(''); setResult(null); setSelected([]);
    const params: Record<string, string | number> = { current_page: query.currentPage, per_page: perPage };
    Object.entries(query.filters).forEach(([key, value]) => {
      if (value && value !== 'all' && (isUserlist || key !== 'ban_status')) params[key] = value;
    });
    api.get(isChatMuted ? `${SERVER_ADDRESS}relation/chat-muted-user-list`
      : isUserlist ? url.Communication_url.follow_user_list : url.Communication_url.ban_user_list, { params })
      .then(({ data }) => {
        if (request !== requestId.current) return;
        if (!Array.isArray(data?.userList) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid member list response');
        const lastPage = Math.max(1, Math.ceil(data.total / perPage));
        if (query.currentPage > lastPage) {
          setQuery(previous => ({ ...previous, currentPage: lastPage })); return;
        }
        setResult({ members: data.userList, total: data.total });
      })
      .catch(() => { if (request === requestId.current) setError('회원목록을 불러오지 못했습니다. 다시 시도해주세요.'); })
      .finally(() => { if (request === requestId.current) setLoading(false); });
    return () => { requestId.current += 1; };
  }, [isUserlist, isChatMuted, query]);

  const changeFilter = (key: keyof Filters, value: string) => {
    setDraft(previous => ({ ...previous, [key]: value })); setFilterError('');
  };
  const refresh = () => {
    requestId.current += 1;
    setQuery(previous => ({ ...previous, revision: previous.revision + 1 }));
  };
  const apply = (event: FormEvent) => {
    event.preventDefault();
    if (draft.joined_from && draft.joined_to && draft.joined_from > draft.joined_to) {
      setFilterError('가입 시작일은 종료일보다 늦을 수 없습니다.'); return;
    }
    requestId.current += 1; setFilterError(''); setActionError('');
    setQuery(previous => ({ filters: { ...draft, search_value: draft.search_value.trim() }, currentPage: 1, revision: previous.revision + 1 }));
  };
  const reset = () => {
    requestId.current += 1; setDraft({ ...defaults }); setFilterError(''); setActionError('');
    setQuery(previous => ({ filters: { ...defaults }, currentPage: 1, revision: previous.revision + 1 }));
  };
  const switchList = (target: 'user' | 'block' | 'chat-mute') => {
    requestId.current += 1; setQuery(previous => ({ ...previous, currentPage: 1 }));
    setActionError(''); navigate(`/admin/userlist/${target}`);
  };
  const setCurrentPage: React.Dispatch<React.SetStateAction<number>> = value => {
    requestId.current += 1;
    setQuery(previous => ({ ...previous, currentPage: typeof value === 'function' ? value(previous.currentPage) : value }));
  };
  const requestBan = (members: Member[], banned: boolean, reason = '') => {
    if (mutationPending.current) return;
    const targets = members.filter(member => (member.is_banned === 'y') !== banned);
    if (!targets.length) return;
    setActionError(''); setBanReason(reason); setBanRequest({ members: targets, banned });
  };
  const changeBans = async () => {
    if (mutationPending.current || !banRequest) return;
    const { members, banned } = banRequest;
    const reason = banReason.trim();
    mutationPending.current = true; setActionError(''); setBanResults([]); setRetryRequest(null);
    const results: { member: Member; success: boolean; message?: string }[] = [];
    try {
      for (const member of members) {
        if (mounted.current) setPendingMember(member.user_seq);
        try {
          const { data } = banned
            ? await api.post(url.Communication_url.user_ban, { nickname: member.nickname, ...(reason ? { reason } : {}) })
            : await api.delete(url.Communication_url.user_unban, { data: { nickname: member.nickname } });
          if (data !== '성공') throw new Error('Member update failed');
          results.push({ member, success: true });
        } catch (failure: any) {
          results.push({ member, success: false, message: failure?.response?.data?.code === 'MEMBER_BAN_REASON_UNAVAILABLE'
            ? '차단 사유 저장 준비가 아직 완료되지 않았습니다. 사유 없이 다시 시도할 수 있습니다.' : undefined });
        }
      }
      if (mounted.current) {
        setBanResults(results);
        const failed = results.filter(result => !result.success).map(result => result.member);
        if (failed.length) setRetryRequest({ members: failed, banned, reason });
        setBanRequest(null); refresh();
      }
    } finally { mutationPending.current = false; if (mounted.current) setPendingMember(null); }
  };
  const selectedMembers = result?.members.filter(member => selected.includes(member.user_seq)) || [];

  const releaseChatMute = async (member: Member) => {
    if (mutationPending.current) return;
    mutationPending.current = true; setPendingMember(member.user_seq); setActionError('');
    try {
      const { data } = await api.post(`${SERVER_ADDRESS}realtime/chat-mute`, { nickname: member.nickname, enabled: false });
      if (data?.muted !== false || data?.nickname !== member.nickname) throw new Error('Unconfirmed update');
      if (mounted.current) refresh();
    } catch { if (mounted.current) setActionError('채팅 금지를 해제하지 못했습니다. 다시 시도해주세요.'); }
    finally { mutationPending.current = false; if (mounted.current) setPendingMember(null); }
  };

  return <Layout_Admin menuname="회원목록">
    <AdminPageHeader title="회원 관리" description={isChatMuted ? "채팅 금지된 회원을 관리합니다. 현재 방송에 적용되며 방송 종료 시 자동 해제됩니다." : isUserlist ? "이 스토어를 팔로우한 회원을 조회합니다." : "이 스토어에서 차단한 회원을 조회합니다."} />
    <Tabs aria-label="회원목록 구분">
      <button type="button" aria-current={isUserlist ? 'page' : undefined} onClick={() => switchList('user')}>회원목록</button>
      <button type="button" aria-current={!isUserlist && !isChatMuted ? 'page' : undefined} onClick={() => switchList('block')}>블랙리스트</button>
      <button type="button" aria-current={isChatMuted ? 'page' : undefined} onClick={() => switchList('chat-mute')}>채팅 금지</button>
    </Tabs>
    <FilterForm data-admin-search="" aria-label="회원 검색 필터" onSubmit={apply}>
      <FilterGrid>
        <Field>가입 경로<select value={draft.join_route} onChange={e => changeFilter('join_route', e.target.value)}>
          <option value="all">전체</option><option value="normal">팬버드</option><option value="awake">AWAKE</option>
        </select></Field>
        <Field>회원 상태<select value={draft.member_status} onChange={e => changeFilter('member_status', e.target.value)}>
          <option value="all">전체</option><option value="active">활동 회원</option><option value="withdrawn">탈퇴 회원</option>
        </select></Field>
        {isUserlist && <Field>차단 여부<select value={draft.ban_status} onChange={e => changeFilter('ban_status', e.target.value)}>
          <option value="all">전체</option><option value="unbanned">미차단</option><option value="banned">차단</option>
        </select></Field>}
        <Field>가입 시작일<input type="date" value={draft.joined_from} max={draft.joined_to || '9999-12-31'} onChange={e => changeFilter('joined_from', e.target.value)} /></Field>
        <Field>가입 종료일<input type="date" value={draft.joined_to} min={draft.joined_from || undefined} max="9999-12-31" onChange={e => changeFilter('joined_to', e.target.value)} /></Field>
      </FilterGrid>
      <SearchRow>
        <Field>검색 항목<select value={draft.search_key} onChange={e => changeFilter('search_key', e.target.value)}>
          <option value="nickname">닉네임</option><option value="name">입금자명</option><option value="phone">전화번호</option><option value="userid">회원 아이디</option>
        </select></Field>
        <Field className="keyword">검색어<input type="search" maxLength={100} placeholder="검색어 없이 필터만 적용할 수도 있습니다."
          value={draft.search_value} onChange={e => changeFilter('search_value', e.target.value)} /></Field>
        <Button type="submit">검색</Button><Button data-admin-search-reset="" type="button" className="secondary" onClick={reset}>초기화</Button>
      </SearchRow>
      {filterError && <ErrorText role="alert">{filterError}</ErrorText>}
    </FilterForm>
    {actionError && <ErrorText role="alert">{actionError}</ErrorText>}
    {banResults.length > 0 && <ActionResults aria-label="차단 처리 결과" role="status">
      <p>처리 완료 {banResults.filter(result => result.success).length}명 · 확인 필요 {banResults.filter(result => !result.success).length}명</p>
      <ul>{banResults.map(result => <li key={result.member.user_seq}>{result.member.nickname}: {result.success ? '완료' : result.message || '처리 실패 · 목록에서 현재 상태를 확인해주세요.'}</li>)}</ul>
      {retryRequest && <Button type="button" className="secondary" disabled={pendingMember !== null}
        onClick={() => requestBan(retryRequest.members, retryRequest.banned, retryRequest.reason)}>실패한 회원만 다시 시도</Button>}
    </ActionResults>}
    <Results aria-live="polite" aria-busy={loading}>
      {loading ? <Message role="status">회원목록을 불러오는 중입니다.</Message>
        : error ? <Message role="alert">{error}<Button type="button" className="secondary" onClick={refresh}>다시 시도</Button></Message>
        : result && <>
          <Count>검색 결과 <strong>{result.total.toLocaleString()}명</strong></Count>
          {!isChatMuted && <BulkActions aria-label="선택 회원 관리">
            <label><input type="checkbox" aria-label="현재 페이지 전체 선택" disabled={pendingMember !== null || result.members.length === 0}
              checked={result.members.length > 0 && selectedMembers.length === result.members.length}
              onChange={event => setSelected(event.target.checked ? result.members.map(member => member.user_seq) : [])} /> 현재 페이지 전체 선택</label>
            <span>선택 {selectedMembers.length}명</span>
            <Button type="button" className="compact" disabled={pendingMember !== null || !selectedMembers.some(member => member.is_banned !== 'y')}
              onClick={() => requestBan(selectedMembers, true)}>선택 차단</Button>
            <Button type="button" className="secondary compact" disabled={pendingMember !== null || !selectedMembers.some(member => member.is_banned === 'y')}
              onClick={() => requestBan(selectedMembers, false)}>선택 차단 해제</Button>
          </BulkActions>}
          <TableScroll><Table>
            <thead><tr><th>번호</th><th>닉네임</th><th>입금자명</th><th>연락처</th><th>주소</th><th>가입 경로</th><th>회원 상태</th><th>가입일자</th><th>마지막 활동</th>{!isChatMuted && <><th>차단 사유</th><th>선택</th></>}<th>{isChatMuted ? '채팅 금지' : '차단'}</th></tr></thead>
            <tbody>{result.members.length === 0 ? <tr><td colSpan={isChatMuted ? 10 : 12}><Message>조건에 맞는 회원이 없습니다.</Message></td></tr>
              : result.members.map((member, index) => <tr key={member.user_seq}>
                <td>{result.total - perPage * (query.currentPage - 1) - index}</td>
                <td>{member.nickname || '—'}</td><td>{member.name || '—'}</td><td>{member.phone || '—'}</td>
                <td className="address">{[member.addr_basic, member.addr_detail].filter(Boolean).join(' ') || '—'}</td>
                <td>{routeLabel(member.join_route)}</td><td>{member.withdrawal_dt ? '탈퇴' : member.withdrawal_dt === null ? '활동' : '미확인'}</td>
                <td>{dateLabel(member.join_dt)}</td><td>{activityLabel(member.last_dt)}</td>
                {!isChatMuted && <><td className="reason">{member.is_banned === 'y' ? member.ban_reason?.trim() || '등록된 사유 없음' : '—'}</td>
                  <td><input type="checkbox" aria-label={`${member.nickname} 선택`} disabled={pendingMember !== null}
                    checked={selected.includes(member.user_seq)} onChange={event => setSelected(current => event.target.checked
                      ? [...current, member.user_seq] : current.filter(id => id !== member.user_seq))} /></td></>}
                <td><Button type="button" className={isChatMuted || member.is_banned === 'y' ? 'secondary compact' : 'compact'}
                  disabled={pendingMember !== null} onClick={() => isChatMuted ? releaseChatMute(member) : requestBan([member], member.is_banned !== 'y')}>
                  {pendingMember === member.user_seq ? '처리 중' : isChatMuted ? '채팅 금지 해제' : member.is_banned === 'y' ? '차단 해제' : '차단하기'}
                </Button></td>
              </tr>)}</tbody>
          </Table></TableScroll>
          {result.total > 0 && <PagingControl cnt={result.total} currentPage={query.currentPage} setCurrentPage={setCurrentPage} perPage={perPage} />}
        </>}
    </Results>
    {banRequest && <LiveDetailDialog open title={banRequest.banned ? '회원 차단' : '회원 차단 해제'} size="compact"
      onClose={() => { if (!mutationPending.current) setBanRequest(null); }} footer={<>
        <Button type="button" className="secondary" disabled={pendingMember !== null} onClick={() => setBanRequest(null)}>취소</Button>
        <Button type="button" disabled={pendingMember !== null} onClick={() => void changeBans()}>{pendingMember !== null ? '처리 중' : '확인'}</Button>
      </>}>
      <p>{banRequest.members.length === 1 ? `${banRequest.members[0].nickname} 님` : `선택한 ${banRequest.members.length}명`}을 {banRequest.banned ? '차단' : '차단 해제'}하시겠습니까?</p>
      {banRequest.banned && <ReasonField>차단 사유 (선택)<textarea maxLength={500} value={banReason} disabled={pendingMember !== null}
        onChange={event => setBanReason(event.target.value)} placeholder="선택한 회원에게 동일한 사유가 저장됩니다." /></ReasonField>}
    </LiveDetailDialog>}
  </Layout_Admin>;
}

const Tabs = styled.nav.attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  display: flex; gap: 24px; border-bottom: 1px solid var(--admin-border); margin-bottom: 20px;
  button { border: 0; border-bottom: 2px solid transparent; background: none; padding: 0 0 14px; font-size: 15px; color: var(--admin-muted); cursor: pointer; }
  button[aria-current='page'] { color: var(--admin-ink); border-bottom-color: var(--admin-theme-link, var(--admin-accent)); font-weight: 600; }
`;
const FilterForm = styled.form`padding: 20px; border: 1px solid var(--admin-theme-border, #e5e7eb); border-radius: 12px; background: var(--admin-theme-surface, #fafafa); margin-bottom: 24px;`;
const FilterGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px;`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 8px; min-width: 0; font-size: 13px; color: var(--admin-theme-text, #444);
  input, select { box-sizing: border-box; width: 100%; min-width: 0; height: 44px; border: 1px solid var(--admin-theme-border, #d9dde3); border-radius: 7px; padding: 0 12px; background: var(--admin-theme-surface, #fff); font: inherit; font-size: 15px; color: var(--admin-theme-text, #252525); }
  input:focus, select:focus { outline: 2px solid var(--admin-accent); outline-offset: 1px; }
  input::placeholder { color: var(--admin-muted); }
`;
const SearchRow = styled.div`
  display: flex; gap: 12px; align-items: flex-end; margin-top: 18px; flex-wrap: wrap;
  > label:first-child { width: 145px; } .keyword { flex: 1; min-width: 200px; }
`;
const Button = styled.button`
  flex-shrink: 0; min-height: 44px; padding: 10px 20px; border: 1px solid var(--admin-ink); border-radius: 7px; background: var(--admin-solid, #292433); color: white; font-size: 14px; cursor: pointer;
  &.secondary { background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #333); border-color: var(--admin-theme-border, #d4d8dd); }
  &.compact { padding: 7px 12px; min-height: 36px; white-space: nowrap; }
  &:disabled { opacity: .5; cursor: default; }
  &:focus-visible { outline: 2px solid var(--admin-accent); outline-offset: 2px; }
`;
const Results = styled.section`min-height: 160px;`;
const Count = styled.p`font-size: 14px; margin-bottom: 14px; strong { margin-left: 4px; }`;
const TableScroll = styled.div`overflow-x: auto; border: 1px solid var(--admin-theme-border, #e5e7eb); border-radius: 8px;`;
const Table = styled.table`
  width: 100%; min-width: 1050px; border-collapse: collapse; text-align: center; font-size: 14px;
  ${sellerTable}
  td.address, td.reason { min-width: 160px; max-width: 270px; text-align: left; overflow-wrap: anywhere; }
  td.reason { white-space: pre-wrap; }
`;
const Message = styled.div`padding: 48px 16px; text-align: center; color: var(--admin-theme-muted, #68707b); button { display: block; margin: 16px auto 0; }`;
const ErrorText = styled.p`margin: 12px 0; color: var(--admin-theme-danger, #c02b43); font-size: 14px;`;

const BulkActions = styled.div`display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin:0 0 14px;font-size:14px;label{display:flex;align-items:center;gap:6px;}input{width:18px;height:18px;}`;
const ReasonField = styled.label`display:flex;flex-direction:column;gap:8px;margin-top:16px;font-size:14px;textarea{box-sizing:border-box;width:100%;min-height:100px;resize:vertical;padding:10px;border:1px solid var(--admin-theme-border,#d4d8dd);border-radius:7px;background:var(--admin-theme-surface,#fff);color:var(--admin-theme-text,#333);font:inherit;}`;
const ActionResults = styled.section`margin:16px 0;padding:14px;border:1px solid var(--admin-theme-border,#d4d8dd);border-radius:8px;font-size:14px;p{margin:0 0 8px;}ul{margin:0 0 12px;padding-left:20px;}`;
